import { Flex, Text } from '@chakra-ui/react';
import { faEdit, faPlus, faTag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, ButtonText, Link, Modal } from '@morressier/alembic';
import { useState } from 'react';
import AddingTagsModal from './AddingTagsModal';
import { useQuery } from '@tanstack/react-query';
import { fetchEditorTags, fetchSubmissionContextTags } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import ProtectedAction from '../../components/ProtectedAction';
import TagDetailsPopover from 'src/components/Submission/AuthorsTab/AuthorProfileModal/TagDetailsPopover';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const ManageTags = () => {
  const [showAddTagModal, setShowAddTagModal] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { shortname } = useParams();
  const { workflowResponse } = useSubmissionData();
  const { submissionId, workflowData, submissionContextId, cloneSource } =
    workflowResponse;

  const { data: manageTags } = useQuery({
    queryKey: ['submission-context-tags', { submissionContextId }],
    queryFn: () =>
      submissionContextId
        ? fetchSubmissionContextTags(submissionContextId)
        : null
  });

  const manageTagsData = manageTags?.collection ?? [];
  const data = manageTagsData.filter(tag => tag.usage === 'submissions');

  const { data: allTagsData } = useQuery({
    queryKey: ['editor-tags', { submissionId }],
    queryFn: () => (submissionId ? fetchEditorTags(submissionId) : null)
  });
  const allTagsRow = allTagsData?.response ?? [];
  const allTags = allTagsRow.filter(tag => tag.usage === 'submissions');

  const tagsExist = allTags?.length > 0;
  const MAX_VISIBLE = 2;
  const showClonedBadge = !!cloneSource?.previousSubmissionId;

  const remainingCount = allTags.length - MAX_VISIBLE;
  const shouldShowToggle = allTags.length > MAX_VISIBLE;
  const currentLocationState =
    (location.state as { from?: string; parentFrom?: string } | null) ?? null;

  const handleOpenClonedFromSubmission = () => {
    if (!shortname || !cloneSource?.previousSubmissionId) {
      return;
    }

    navigate(
      `/app/workspace/${shortname}/j/${submissionContextId}/submissions?selectedItem=${cloneSource.previousSubmissionId}&activeTab=overview`,
      {
        state: {
          from: location.pathname + location.search,
          parentFrom: currentLocationState?.from
        }
      }
    );
  };

  return (
    <>
      {showAddTagModal ? (
        <AddingTagsModal
          isOpen={showAddTagModal}
          onClose={() => setShowAddTagModal(false)}
          title={
            allTags?.length
              ? 'Update Tags to Submission'
              : 'Add Tags to Submission'
          }
          data={data}
          allTags={allTags?.length ? allTags : []}
          buttonText={
            allTags?.length ? 'Update Selected Tags' : 'Add Selected Tags'
          }
        />
      ) : null}
      {showAll && (
        <Modal isOpen={showAll} width="720px" onClose={() => setShowAll(false)}>
          <Flex padding={4} flexDirection="column" gap={4}>
            <Text textStyle="headingGroup">Tags</Text>
            <Flex flexDirection="row" gap={2} flexWrap="wrap">
              {allTags.map((tag, idx) => (
                <Flex key={idx} align="center" gap={1}>
                  <TagDetailsPopover tagDetail={tag} />
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Modal>
      )}

      <Flex gap={2} mt={1} alignItems="center">
        <FontAwesomeIcon icon={faTag} />
        <Flex gap={2} flexWrap="wrap">
          {allTags?.length ? (
            allTags
              .slice(0, MAX_VISIBLE)
              .map(tag => <TagDetailsPopover tagDetail={tag} />)
          ) : (
            <Text textStyle="textContext">
              No Tags added to this submission yet.
            </Text>
          )}
          {shouldShowToggle && (
            <ButtonText
              variant="outline"
              fontSize={'sm'}
              onClick={() => setShowAll(prev => !prev)}
            >
              {showAll
                ? 'Show Less'
                : `+${remainingCount} tag${remainingCount > 1 ? 's' : ''}`}
            </ButtonText>
          )}
        </Flex>
        <Flex>
          <ProtectedAction
            relation="task_completer"
            entityType="journal_phase"
            entityId={`${submissionContextId}_${workflowData.currentState}`}
            tooltipLabel={
              <span>You do not have permission to perform this action</span>
            }
          >
            <ButtonText
              size="regular"
              variant="primary"
              fontSize={'sm'}
              iconLeft={<FontAwesomeIcon icon={tagsExist ? faEdit : faPlus} />}
              onClick={() => setShowAddTagModal(true)}
            >
              {tagsExist ? 'Edit' : 'Add tags'}
            </ButtonText>
          </ProtectedAction>
        </Flex>
        {showClonedBadge && (
          <Flex alignItems="center" gap={2} flexWrap="wrap">
            <Badge size="context" variant="neutral">
              Cloned from:
            </Badge>
            <Link
              textStyle="textContext"
              variant="primary"
              onClick={handleOpenClonedFromSubmission}
            >
              {cloneSource?.previousManuscriptId}
            </Link>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default ManageTags;
